import { Form, Grid, SectionItem } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import HTML_MAIL_RELANCE_AVANT_ECHEANCE_DEFAUT from '@/assets/text/htmlMailRelanceAvantEcheanceDefaut';
import TEXTE_RELANCE_AVANT_ECHEANCE_DEFAUT from '@/assets/text/texteRelanceAvantEcheanceDefaut';
import {
  RELANCE_AVANT_ECHEANCE_ELEMENTS_DISPONIBLES,
  RELANCE_AVANT_ECHEANCE_MAIL_ELEMENTS_DISPONIBLES,
} from '@/constants';
import { printRelance, printRelanceAvantEcheance } from '@/routes';
import type { ErrorFieldType } from '@/types';

import { Relance } from '../../Relance';

type RegieProlongeeFormProps = {
  identifiant?: string | number;
  errors?: ErrorFieldType[];
  onChange?: (e: any) => void;
  regieProlongee?: boolean;
  joursEncaissementSupplementaires?: number;
  updated?: boolean;
  // Relances après date limite de paiement (comportement historique)
  envoiRelanceParMail?: boolean;
  seuilRelance?: number | string;
  texteRelance?: string;
  htmlMailRelance?: string;
  // Relances avant date limite de paiement
  envoiRelanceAvantEcheanceParMail?: boolean;
  seuilRelanceAvantEcheance?: number | string;
  texteRelanceAvantEcheance?: string;
  htmlMailRelanceAvantEcheance?: string;
};

const RegieProlongee = (props: RegieProlongeeFormProps) => {
  const {
    identifiant,
    errors,
    regieProlongee = false,
    joursEncaissementSupplementaires,
    updated,
    envoiRelanceParMail = false,
    seuilRelance,
    texteRelance,
    htmlMailRelance,
    envoiRelanceAvantEcheanceParMail = false,
    seuilRelanceAvantEcheance,
    texteRelanceAvantEcheance,
    htmlMailRelanceAvantEcheance,
    onChange,
  } = props;

  const handleRegieProlongeeChange = React.useCallback(
    (e, { checked }) => {
      if (onChange) {
        onChange({
          identifiant,
          regieProlongee: checked,
          joursEncaissementSupplementaires: 1,
          seuilRelance: 15,
          ...(!checked && {
            joursEncaissementSupplementaires: null,
            envoiRelanceParMail: false,
            seuilRelance: null,
          }),
        });
      }
    },
    [identifiant, onChange],
  );

  const handleChange = React.useCallback(
    (event, data) => {
      const { name, value } = data;

      if (onChange) {
        if (name === 'joursEncaissementSupplementaires') {
          const numberValue = value === '' ? null : _.toNumber(value);

          onChange({
            identifiant,
            [name]: _.isEmpty(value)
              ? null
              : numberValue > 99
                ? 99
                : numberValue < 1
                  ? 1
                  : numberValue,
          });
        }
      }
    },
    [identifiant, onChange],
  );

  const handleJoursBlur = React.useCallback(() => {
    if (onChange) {
      onChange({
        identifiant,
        joursEncaissementSupplementaires:
          _.isNil(joursEncaissementSupplementaires) || joursEncaissementSupplementaires <= 1
            ? 1
            : joursEncaissementSupplementaires,
      });
    }
  }, [identifiant, onChange, joursEncaissementSupplementaires]);

  return (
    <Grid stackable relaxed>
      <Grid.Row style={{ paddingTop: 0 }}>
        {/* Relances AVANT date limite de paiement : toujours actives, indépendantes de la régie prolongée */}
        <Grid.Column width={8} style={{ paddingLeft: 0 }}>
          <Form>
            <SectionItem title="Relances avant date limite de paiement" single compact>
              <Relance
                withSection={false}
                identifiant={identifiant}
                errors={errors}
                disabled={false}
                names={{
                  seuil: 'seuilRelanceAvantEcheance',
                  envoi: 'envoiRelanceAvantEcheanceParMail',
                  texte: 'texteRelanceAvantEcheance',
                  html: 'htmlMailRelanceAvantEcheance',
                }}
                elements={RELANCE_AVANT_ECHEANCE_ELEMENTS_DISPONIBLES}
                texteDefaut={TEXTE_RELANCE_AVANT_ECHEANCE_DEFAUT}
                mailElements={RELANCE_AVANT_ECHEANCE_MAIL_ELEMENTS_DISPONIBLES}
                htmlMailDefaut={HTML_MAIL_RELANCE_AVANT_ECHEANCE_DEFAUT}
                printRoute={printRelanceAvantEcheance}
                testIdPrefix="relance-avant-echeance"
                envoiRelanceParMail={envoiRelanceAvantEcheanceParMail}
                seuilRelance={seuilRelanceAvantEcheance}
                texteRelance={texteRelanceAvantEcheance}
                htmlMailRelance={htmlMailRelanceAvantEcheance}
                updated={updated}
                onChange={onChange}
              />
            </SectionItem>
          </Form>
        </Grid.Column>

        {/* Relances APRÈS date limite de paiement : coche régie prolongée + jours, puis champs relance (comportement historique) */}
        <Grid.Column width={8} style={{ paddingRight: 0 }}>
          <Form>
            <SectionItem title="Relances après date limite de paiement" single compact>
              <Form.Group
                style={{
                  display: 'flex',
                }}
                inline
              >
                <Form.Checkbox
                  width={6}
                  data-testid="regie-prolongee-checkbox-regie-prolongee"
                  toggle
                  label="Régie prolongée"
                  checked={regieProlongee}
                  onChange={handleRegieProlongeeChange}
                  name="regieProlongee"
                />
                <Form.Field
                  // @ts-expect-error
                  id="joursEncaissementSupplementaires"
                  width={10}
                  required={regieProlongee}
                  disabled={!regieProlongee}
                  error={_.some(errors, { id: 'joursEncaissementSupplementaires' })}
                >
                  <label>Nombre de jours d&apos;encaissement supplémentaires permis</label>
                  <Form.Input
                    type="number"
                    input={{
                      'data-testid': 'regie-prolongee-input-jours-encaissement-supplementaires',
                    }}
                    style={{ width: 50 }}
                    name="joursEncaissementSupplementaires"
                    value={
                      joursEncaissementSupplementaires <= 0 ||
                      _.isNil(joursEncaissementSupplementaires)
                        ? ''
                        : joursEncaissementSupplementaires
                    }
                    onChange={handleChange}
                    onBlur={handleJoursBlur}
                    onKeyDown={(evt) =>
                      ['e', 'E', '+', '-', '.', ','].includes(evt.key) && evt.preventDefault()
                    }
                    onFocus={(e) => {
                      e.target.select();
                    }}
                  />
                </Form.Field>
              </Form.Group>

              <Relance
                withSection={false}
                identifiant={identifiant}
                errors={errors}
                disabled={!regieProlongee}
                names={{
                  seuil: 'seuilRelance',
                  envoi: 'envoiRelanceParMail',
                  texte: 'texteRelance',
                  html: 'htmlMailRelance',
                }}
                printRoute={printRelance}
                testIdPrefix="regie-prolongee"
                envoiRelanceParMail={envoiRelanceParMail}
                seuilRelance={seuilRelance}
                texteRelance={texteRelance}
                htmlMailRelance={htmlMailRelance}
                updated={updated}
                onChange={onChange}
              />
            </SectionItem>
          </Form>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default RegieProlongee;
